import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
    CircularProgress,
    IconButton,
    Tooltip,
    Divider,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    useTheme
} from '@mui/material';
import {
    CloudUpload,
    Image,
    VideoLibrary,
    Description,
    Edit,
    Delete,
    Add,
    Link
} from '@mui/icons-material';
import { detectFilesInText, DetectedFile, replaceFileInText, FileReplacement, addFileToText } from '../utils/fileDetection';
import { fileUploadApi } from '../utils/fileUploadApi';
import { mediaApi } from '../utils/mediaApi';

interface FileManagerProps {
    promptText: string;
    onTextChange: (newText: string) => void;
    disabled?: boolean;
    onAutoSave?: () => void;
    agentId?: string; // ID do agente para associar a mídia
}

interface FileUploadDialogProps {
    open: boolean;
    onClose: () => void;
    onUpload: (file: File) => Promise<void>;
    loading: boolean;
    fileType: 'image' | 'video' | 'document';
}

const FileUploadDialog: React.FC<FileUploadDialogProps> = ({
    open,
    onClose,
    onUpload,
    loading,
    fileType
}) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const theme = useTheme();

    const acceptedTypes = {
        image: '.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg',
        video: '.mp4,.avi,.mov,.wmv,.flv,.webm,.mkv',
        document: '.pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z'
    };

    const typeLabels = {
        image: 'Imagem',
        video: 'Vídeo',
        document: 'Documento'
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUpload = async () => {
        if (selectedFile) {
            await onUpload(selectedFile);
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleClose = () => {
        setSelectedFile(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                Adicionar {typeLabels[fileType]}
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 2 }}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept={acceptedTypes[fileType]}
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                    />
                    <Button
                        variant="outlined"
                        startIcon={<CloudUpload />}
                        onClick={() => fileInputRef.current?.click()}
                        fullWidth
                        sx={{ mb: 2 }}
                    >
                        Selecionar Arquivo
                    </Button>
                    {selectedFile && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Arquivo selecionado: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                        </Alert>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={loading}>
                    Cancelar
                </Button>
                <Button
                    onClick={handleUpload}
                    variant="contained"
                    sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                    disabled={!selectedFile || loading}
                    startIcon={loading ? <CircularProgress size={18} /> : <CloudUpload />}
                >
                    {loading ? 'Enviando...' : 'Enviar'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const FileManager: React.FC<FileManagerProps> = ({
    promptText,
    onTextChange,
    disabled = false,
    onAutoSave,
    agentId
}) => {
    const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
    const [uploadLoading, setUploadLoading] = useState(false);
    const [uploadFileType, setUploadFileType] = useState<'image' | 'video' | 'document'>('image');
    const [mediaLimitReached, setMediaLimitReached] = useState(false);
    const [mediaLimitInfo, setMediaLimitInfo] = useState<{ currentCount: number; maxMedia: number; isUnlimited: boolean } | null>(null);

    const theme = useTheme();
    const detectedFiles = detectFilesInText(promptText);
    const filesToReplace = detectedFiles.filter(file => !file.isFromCDN);
    const hasFiles = detectedFiles.length > 0;

    // Função para recarregar limite de mídias
    const reloadMediaLimit = async () => {
        try {
            const info = await mediaApi.getMediaLimitInfo();
            setMediaLimitInfo(info);
            setMediaLimitReached(!info.canUpload);
            console.log('Limite de mídias atualizado:', info);
            return info;
        } catch (error) {
            console.error('Erro ao carregar limite de mídias:', error);
            return null;
        }
    };

    // Carregar informações de limite de mídias ao montar
    useEffect(() => {
        reloadMediaLimit();
    }, []);

    const handleUpload = async (file: File) => {
        // Verificar limite de mídias antes de fazer upload
        if (mediaLimitReached) {
            alert(`Limite de mídias atingido! Seu plano permite até ${mediaLimitInfo?.maxMedia} mídias. Remova algumas mídias ou atualize seu plano.`);
            return;
        }

        setUploadLoading(true);
        try {
            const result = await fileUploadApi.uploadFile(file);
            console.log('Upload concluído:', result);

            const newText = addFileToText(promptText, result.url, uploadFileType);
            onTextChange(newText);
            setUploadDialogOpen(false);

            // Associar mídia ao agente se tiver agentId
            console.log('🔍 Verificando associação - agentId:', agentId, 'mediaId:', result.mediaId, 'result completo:', result);

            if (agentId && result.mediaId) {
                try {
                    console.log('✅ Tentando associar mídia ao agente:', { mediaId: result.mediaId, agentId });
                    await mediaApi.updateMediaAgent(result.mediaId, agentId);
                    console.log('✅ Mídia associada com sucesso ao agente!');
                } catch (error: any) {
                    console.error('❌ Erro ao associar mídia ao agente:', error);
                    console.error('Detalhes do erro:', error.message, error.response);
                    alert('Aviso: Arquivo enviado mas não foi possível associar ao agente. Verifique o gerenciamento de mídias.');
                }
            } else {
                if (!agentId) {
                    console.warn('⚠️ Agente ainda não foi criado. MediaId:', result.mediaId);
                } else if (!result.mediaId) {
                    console.error('❌ Backend não retornou mediaId. Resposta completa:', result);
                }
            }

            // Atualização otimista do contador (imediata)
            if (mediaLimitInfo) {
                const newCount = mediaLimitInfo.currentCount + 1;
                setMediaLimitInfo({
                    ...mediaLimitInfo,
                    currentCount: newCount
                });
                setMediaLimitReached(!mediaLimitInfo.isUnlimited && newCount >= mediaLimitInfo.maxMedia);
            }

            // Salvamento automático se a função estiver disponível
            if (onAutoSave) {
                await onAutoSave();
            }

            // Confirmar com backend após operações
            setTimeout(() => reloadMediaLimit(), 500);
        } catch (error: any) {
            console.error('Erro no upload:', error);
            if (error.response?.data?.includes('Limite de mídias atingido')) {
                alert(error.response.data);
                setMediaLimitReached(true);
            } else {
                alert('Erro ao fazer upload do arquivo');
            }
        } finally {
            setUploadLoading(false);
        }
    };

    const handleReplaceFile = async (originalUrl: string, file: File) => {
        setUploadLoading(true);
        try {
            const result = await fileUploadApi.uploadFile(file);
            console.log('🔄 Substituindo arquivo - Upload concluído:', result);

            // Encontrar o arquivo a ser substituído
            const fileToReplace = detectedFiles.find(f => f.originalUrl === originalUrl);
            if (fileToReplace) {
                const replacement: FileReplacement = {
                    originalUrl,
                    newUrl: result.url,
                    startIndex: fileToReplace.startIndex,
                    endIndex: fileToReplace.endIndex
                };

                const newText = replaceFileInText(promptText, replacement);
                onTextChange(newText);

                // Associar mídia ao agente se tiver agentId
                if (agentId && result.mediaId) {
                    try {
                        console.log('✅ Associando mídia substituída ao agente:', { mediaId: result.mediaId, agentId });
                        await mediaApi.updateMediaAgent(result.mediaId, agentId);
                        console.log('✅ Mídia substituída associada com sucesso!');
                    } catch (error: any) {
                        console.error('❌ Erro ao associar mídia substituída:', error);
                    }
                }

                // Atualização otimista do contador (substituição não muda o total)
                // Mas vamos recarregar para garantir
                setTimeout(() => reloadMediaLimit(), 500);

                // Salvamento automático se a função estiver disponível
                if (onAutoSave) {
                    await onAutoSave();
                }
            }
        } catch (error) {
            console.error('Erro no upload:', error);
            alert('Erro ao fazer upload do arquivo');
        } finally {
            setUploadLoading(false);
        }
    };

    const handleRemoveFile = async (originalUrl: string) => {
        // Verificar se é um arquivo do nosso CDN
        const isFromOurCDN = originalUrl.includes('https://gaia-plus.nyc3.digitaloceanspaces.com');

        if (isFromOurCDN) {
            try {
                setUploadLoading(true);
                await fileUploadApi.deleteFile(originalUrl);
                console.log('Arquivo deletado do CDN:', originalUrl);

                // Atualização otimista do contador (imediata)
                if (mediaLimitInfo) {
                    const newCount = Math.max(0, mediaLimitInfo.currentCount - 1);
                    setMediaLimitInfo({
                        ...mediaLimitInfo,
                        currentCount: newCount
                    });
                    setMediaLimitReached(!mediaLimitInfo.isUnlimited && newCount >= mediaLimitInfo.maxMedia);
                }
            } catch (error) {
                console.error('Erro ao deletar arquivo do CDN:', error);
                // Continuar com a remoção do texto mesmo se falhar no CDN
            } finally {
                setUploadLoading(false);
            }
        }

        // Remover do texto do prompt
        const newText = promptText.replace(originalUrl, '').replace(/\n\s*\n/g, '\n').trim();
        onTextChange(newText);

        // Salvamento automático se a função estiver disponível
        if (onAutoSave) {
            await onAutoSave();
        }

        // Confirmar com backend após operações
        setTimeout(() => reloadMediaLimit(), 500);
    };

    const openUploadDialog = (fileType: 'image' | 'video' | 'document') => {
        if (mediaLimitReached) {
            alert(`Limite de mídias atingido! Seu plano permite até ${mediaLimitInfo?.maxMedia} mídias. Remova algumas mídias ou atualize seu plano.`);
            return;
        }
        setUploadFileType(fileType);
        setUploadDialogOpen(true);
    };

    const getFileIcon = (fileType: 'image' | 'video' | 'document') => {
        switch (fileType) {
            case 'image': return <Image />;
            case 'video': return <VideoLibrary />;
            case 'document': return <Description />;
        }
    };

    const getFileTypeColor = (fileType: 'image' | 'video' | 'document') => {
        switch (fileType) {
            case 'image': return 'success';
            case 'video': return 'primary';
            case 'document': return 'warning';
            default: return 'default';
        }
    };

    if (disabled) {
        return null;
    }

    return (
        <Box sx={{ mt: 2 }}>
            {/* Aviso de limite de mídias */}
            {mediaLimitInfo && !mediaLimitInfo.isUnlimited && (
                <Alert
                    severity={mediaLimitReached ? 'error' : (mediaLimitInfo.currentCount / mediaLimitInfo.maxMedia > 0.8) ? 'warning' : 'info'}
                    sx={{ mb: 2 }}
                >
                    {mediaLimitReached ? (
                        `Limite de mídias atingido! Você está usando ${mediaLimitInfo.currentCount} de ${mediaLimitInfo.maxMedia} mídias permitidas pelo seu plano.`
                    ) : (
                        `Mídias: ${mediaLimitInfo.currentCount} / ${mediaLimitInfo.maxMedia} (${Math.round((mediaLimitInfo.currentCount / mediaLimitInfo.maxMedia) * 100)}% usado)`
                    )}
                </Alert>
            )}

            {/* Aviso quando agente ainda não foi criado */}
            {!agentId && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Nota: Este agente ainda não foi salvo. As mídias adicionadas ficarão como "Não Utilizadas" até que você salve o agente. Depois, use "Verificar Não Utilizadas" no gerenciamento de mídias para associá-las.
                </Alert>
            )}

            {/* Seção de arquivos detectados */}
            {hasFiles && (
                <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Link fontSize="small" />
                        Arquivos Detectados ({detectedFiles.length})
                    </Typography>

                    <List dense>
                        {detectedFiles.map((file, index) => (
                            <ListItem key={index} sx={{ px: 0, flexDirection: 'column', alignItems: 'stretch' }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                        {getFileIcon(file.fileType!)}
                                        <Typography variant="body2" sx={{ flex: 1, wordBreak: 'break-all' }}>
                                            {file.fileName}
                                        </Typography>
                                        <Chip
                                            label={file.fileType}
                                            size="small"
                                            color={getFileTypeColor(file.fileType!)}
                                            variant={file.isFromCDN ? 'filled' : 'outlined'}
                                            sx={file.isFromCDN ? { color: theme.palette.mode === 'dark' ? '#000' : undefined } : undefined}
                                        />
                                        {!file.isFromCDN && (
                                            <Chip
                                                label="Externo"
                                                size="small"
                                                color="error"
                                                variant="outlined"
                                            />
                                        )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                                        {!file.isFromCDN && (
                                            <Tooltip title="Substituir arquivo">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => {
                                                        const input = document.createElement('input');
                                                        input.type = 'file';
                                                        input.accept = file.fileType === 'image' ? '.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg' :
                                                            file.fileType === 'video' ? '.mp4,.avi,.mov,.wmv,.flv,.webm,.mkv' :
                                                                '.pdf,.doc,.docx,.txt,.rtf,.xls,.xlsx,.ppt,.pptx,.zip,.rar,.7z';
                                                        input.onchange = (e) => {
                                                            const selectedFile = (e.target as HTMLInputElement).files?.[0];
                                                            if (selectedFile) {
                                                                handleReplaceFile(file.originalUrl, selectedFile);
                                                            }
                                                        };
                                                        input.click();
                                                    }}
                                                >
                                                    <Edit fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Tooltip title="Remover arquivo">
                                            <IconButton
                                                size="small"
                                                onClick={() => handleRemoveFile(file.originalUrl)}
                                                color="error"
                                            >
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                                <Typography variant="caption" sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>
                                    {file.originalUrl}
                                </Typography>
                            </ListItem>
                        ))}
                    </List>

                    {filesToReplace.length > 0 && (
                        <Alert severity="warning" sx={{ mt: 1 }}>
                            {filesToReplace.length} arquivo(s) externo(s) detectado(s). Recomendamos substituir por arquivos do nosso CDN. Caso não substitua, o sistema não conseguirá enviar as imagens nas conversas.
                        </Alert>
                    )}
                </Box>
            )}

            {/* Seção de adicionar arquivos */}
            <Box>
                <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Add fontSize="small" />
                    Adicionar Arquivos
                </Typography>

                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button
                        size="small"
                        startIcon={<Image />}
                        onClick={() => openUploadDialog('image')}
                        variant="outlined"
                    >
                        Imagem
                    </Button>
                    <Button
                        size="small"
                        startIcon={<VideoLibrary />}
                        onClick={() => openUploadDialog('video')}
                        variant="outlined"
                    >
                        Vídeo
                    </Button>
                    <Button
                        size="small"
                        startIcon={<Description />}
                        onClick={() => openUploadDialog('document')}
                        variant="outlined"
                    >
                        Documento
                    </Button>
                </Box>
            </Box>

            <FileUploadDialog
                open={uploadDialogOpen}
                onClose={() => setUploadDialogOpen(false)}
                onUpload={handleUpload}
                loading={uploadLoading}
                fileType={uploadFileType}
            />
        </Box>
    );
};

export default FileManager;
